import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import HeaderMarketData from './HeaderMarketData';

// Fade in animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Slide in from left animation
const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Slide in from right animation
const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(0, 194, 255, 0.3);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 15px 0;
    margin-bottom: 10px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  animation: ${slideInLeft} 0.6s ease-out;
  
  @media (max-width: 768px) {
    align-items: center;
    margin-bottom: 15px;
  }
`;

const Logo = styled.h1`
  font-size: 2rem;
  margin: 0 0 5px 0;
  background: linear-gradient(90deg, #00c2ff, #64ffda);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Slogan = styled.p`
  font-size: 0.9rem;
  color: #8892b0;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 25px;
  flex-wrap: wrap;
  justify-content: center;
  animation: ${fadeIn} 0.7s ease-out;
  
  @media (max-width: 768px) {
    width: 100%;
    gap: 15px;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: #c4cdda;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 1rem;
  
  &:hover {
    color: #64ffda;
    background-color: rgba(0, 194, 255, 0.1);
    transform: translateY(-2px);
  }
  
  &.active {
    color: #00c2ff;
    font-weight: 600;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -5px;
    left: 0;
    background: linear-gradient(90deg, #00c2ff, #64ffda);
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 6px 10px;
  }
`;

const AuthButtons = styled.div`
  display: flex;
  gap: 15px;
  animation: ${slideInRight} 0.6s ease-out;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    margin-top: 15px;
  }
`;

const LoginButton = styled.a`
  text-decoration: none;
  color: #c4cdda;
  font-weight: 500;
  padding: 10px 20px;
  border-radius: 30px;
  border: 1px solid #00c2ff;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: #00c2ff;
    color: #0c162d;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 194, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.8rem;
  }
`;

const RegisterButton = styled(Link)`
  text-decoration: none;
  color: #0c162d;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 30px;
  background: linear-gradient(90deg, #00c2ff, #64ffda);
  transition: all 0.3s ease;
  font-size: 0.9rem;
  box-shadow: 0 5px 15px rgba(0, 194, 255, 0.4);
  display: flex;
  align-items: center;
  
  &:hover {
    background: linear-gradient(90deg, #64ffda, #00c2ff);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 194, 255, 0.6);
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.8rem;
  }
`;

const DashboardButton = styled(Link)`
  text-decoration: none;
  color: #0c162d;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 30px;
  background: linear-gradient(90deg, #64ffda, #00c2ff);
  transition: all 0.3s ease;
  font-size: 0.9rem;
  box-shadow: 0 5px 15px rgba(100, 255, 218, 0.4);
  display: flex;
  align-items: center;
  
  &:hover {
    background: linear-gradient(90deg, #00c2ff, #64ffda);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 194, 255, 0.6);
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.8rem;
  }
`;

const LogoutButton = styled.button`
  text-decoration: none;
  color: #0c162d;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 30px;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  transition: all 0.3s ease;
  font-size: 0.9rem;
  box-shadow: 0 5px 15px rgba(255, 107, 107, 0.4);
  display: flex;
  align-items: center;
  border: none;
  cursor: pointer;
  
  &:hover {
    background: linear-gradient(90deg, #ff8e8e, #ff6b6b);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 107, 107, 0.6);
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.8rem;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Check if user is authenticated
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

  const handleLogout = () => {
    // Clear authentication state
    localStorage.removeItem('isAuthenticated');
    // Redirect to home page
    window.location.href = '/';
  };

  return (
    <>
      <HeaderMarketData />
      <HeaderContainer>
        <LogoContainer>
          <Logo>UPM Traders</Logo>
          <Slogan>Find regulated and registered brokers with instant withdrawal</Slogan>
        </LogoContainer>
        <Nav>
          <NavLink to="/" style={{ animationDelay: '0.1s' }}>Home</NavLink>
          <NavLink to="/about" style={{ animationDelay: '0.2s' }}>About Us</NavLink>
          <NavLink to="/brokers" style={{ animationDelay: '0.3s' }}>Brokers</NavLink>
          <NavLink to="/instruments" style={{ animationDelay: '0.4s' }}>Instruments</NavLink>
          <NavLink to="/services" style={{ animationDelay: '0.5s' }}>Services</NavLink>
          <NavLink to="/contact" style={{ animationDelay: '0.6s' }}>Contact Us</NavLink>
        </Nav>
        <AuthButtons>
          {isAuthenticated ? (
            <>
              <DashboardButton to="/dashboard">Dashboard</DashboardButton>
              <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
            </>
          ) : (
            <>
              <LoginButton href="/login">Login</LoginButton>
              <RegisterButton to="/register">Register</RegisterButton>
            </>
          )}
        </AuthButtons>
      </HeaderContainer>
    </>
  );
};

export default Header;